import React from 'react';
import PropTypes from 'prop-types';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons/faTrashCan';
import { faGripDotsVertical } from '@fortawesome/pro-solid-svg-icons/faGripDotsVertical';
import { Button, Icon } from '@appcues/sonar';
import { userPropertiesShape } from 'ext/entities/user-properties';
import { BlockContentShape } from 'entities/block/shape';
import ActionsShape from 'lib/actions/shape';
import ActionsConfig from 'components/SideBarSettings/Shared/Actions/ActionsConfig';
import { useDnd, CONDITIONAL_ACTION_CHECK } from 'hooks/use-dnd';
import Conditions from './Conditions';
import {
  Panel,
  RotatingIcon,
  Title,
  DragHandle,
} from './ConditionalAction.styled';

const titleForCondition = (condition, allSurveyBlocks) => {
  const operatorLabels = {
    '==': 'equals',
    '!=': "doesn't equal",
    '*': 'contains',
    '!*': "doesn't contain",
    '^': 'starts with',
    '!^': "doesn't start with",
    $: 'ends with',
    '!$': "doesn't end with",
    regex: 'matches regex',
    in: 'is one of',
    'not in': 'is not one of',
    '>': '>',
    '>=': '≥',
    '<': '<',
    '<=': '≤',
  };

  if (condition?.survey) {
    const { block: blockId, operator, value } = condition.survey;
    const blockTitle =
      allSurveyBlocks.find(block => block.content.id === blockId)?.content.label
        .text ?? '';
    return `If "${blockTitle}" ${operatorLabels[operator] ?? ''} ${value}`;
  }

  if (condition?.token) {
    const { token, operator, value } = condition.token;
    return `If "${token.replace(/[{}]/g, '')}" ${
      operatorLabels[operator] ?? ''
    } ${value}`;
  }

  return condition ? 'If condition' : 'Default';
};

const ConditionalCheckPanel = ({
  check,
  index,
  allSurveyBlocks,
  userProperties,
  contextConfig,
  hasSurveyBlock,
  isExperienceFirstStep,
  onConditionalActionsChange,
  onDelete,
  onAddCondition,
  onDrag,
  onDrop,
}) => {
  const options = { id: check.id, index, onDrag, onDrop };
  const [{ dragging, droppable }, { drag, drop, preview }] = useDnd(
    CONDITIONAL_ACTION_CHECK,
    options
  );

  const hasReachedMaxConditions = index >= 10;

  return (
    <React.Fragment key={check.id}>
      {check.condition === null && !hasReachedMaxConditions && (
        <Button variant="secondary" fluid onClick={onAddCondition}>
          <Icon icon={faPlus} /> Add condition
        </Button>
      )}

      <Panel.Container
        ref={drop}
        open
        dragging={dragging}
        droppable={droppable}
      >
        <Panel.Summary ref={preview}>
          {check.condition && (
            <DragHandle ref={drag} dragging={dragging}>
              <Icon icon={faGripDotsVertical} />
            </DragHandle>
          )}
          <Title>{titleForCondition(check.condition, allSurveyBlocks)}</Title>
          {check.condition && (
            <Button
              aria-label="Delete condition"
              variant="tertiary"
              onClick={() => onDelete(check.id)}
            >
              <Icon icon={faTrashCan} />
            </Button>
          )}
          <RotatingIcon icon={faChevronDown} />
        </Panel.Summary>
        <Panel.Content>
          {check.condition && (
            <Conditions
              condition={check.condition}
              allSurveyBlocks={allSurveyBlocks}
              userProperties={userProperties}
              onActionsChange={onConditionalActionsChange(check.id)}
            />
          )}
          <ActionsConfig
            condition={check.condition}
            actions={check.actions}
            context={contextConfig}
            hasSurveyBlock={hasSurveyBlock}
            userProperties={userProperties}
            allSurveyBlocks={allSurveyBlocks}
            isExperienceFirstStep={isExperienceFirstStep}
            onActionsChange={onConditionalActionsChange(check.id)}
          />
        </Panel.Content>
      </Panel.Container>
    </React.Fragment>
  );
};

ConditionalCheckPanel.propTypes = {
  check: PropTypes.shape({
    id: PropTypes.string,
    condition: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
    actions: PropTypes.arrayOf(ActionsShape),
  }),
  index: PropTypes.number,
  allSurveyBlocks: PropTypes.arrayOf(BlockContentShape),
  userProperties: userPropertiesShape,
  contextConfig: PropTypes.shape({
    label: PropTypes.string,
    description: PropTypes.string,
  }),
  hasSurveyBlock: PropTypes.bool,
  isExperienceFirstStep: PropTypes.bool,
  onConditionalActionsChange: PropTypes.func,
  onDelete: PropTypes.func,
  onAddCondition: PropTypes.func,
  onDrag: PropTypes.func,
  onDrop: PropTypes.func,
};

export default ConditionalCheckPanel;
